import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import { palette } from '@m12s/component-library';
import _get from 'lodash/get';
import { useSelector } from 'react-redux';

import MainLayout from 'components/Layouts/MainLayout';
import {
  findMatchingActivities,
  isActivityDisabled,
} from 'lib/utils/activityMatching';
import { getSwitchActivityTypesOptions } from 'lib/selectors/getSwitchActivityTypesOptions';
import { getOpenActivityTypeRef } from 'lib/selectors/getOpenActivity';
import { throwSuccessToast, throwErrorToast } from 'lib/utils/toast';
import useBarcodeScanHandler from 'lib/hooks/useBarcodeScanHandler';
import { BARCODE_HANDLER_PRIORITIES } from 'lib/constants/barcodePriorities';
import { Table } from './Table';
import { Nav } from './Nav';

const SelectActivityType = ({
  handleOnBack,
  handleOnSelect = () => {},
  handleOnClose,
  jobName,
  job,
  newWorkOrderId,
  isStopAndStartRun,
  workOrderOp,
  showAllTypes,
  ...props
}) => {
  const disallowSetup = _get(job, 'setupTime', undefined) === null;
  const activityTypes = useSelector(getSwitchActivityTypesOptions);
  const openActivityTypeRef = useSelector(getOpenActivityTypeRef);

  // Handle barcode scan events for activity selection
  const handleBarcodeScan = useCallback(
    (barcode) => {
      const matches = findMatchingActivities(barcode, activityTypes);

      if (matches.length === 0) {
        throwErrorToast('Error: Activity not found');
        return;
      }

      if (matches.length > 1) {
        throwErrorToast('Error: Multiple activities found');
        return;
      }

      const matchedActivity = matches[0];

      // Check if the matched activity is disabled
      if (
        isActivityDisabled(matchedActivity.activityTypeRef, openActivityTypeRef)
      ) {
        throwErrorToast('Error: Activity already active');
        return;
      }

      // Success - auto-select the matched activity
      throwSuccessToast(`Activity matched: ${matchedActivity.name}`);
      handleOnSelect(
        matchedActivity.activityTypeRef,
        matchedActivity.activityMode
      );
    },
    [activityTypes, openActivityTypeRef, handleOnSelect]
  );

  // Initialize barcode scanner
  useBarcodeScanHandler('select-activity-page', handleBarcodeScan, {
    priority: BARCODE_HANDLER_PRIORITIES.SELECT_ACTIVITY_PAGE,
  });

  return (
    <MainLayout.Column id="select-activity-type">
      <Nav
        handleOnClose={handleOnClose}
        handleOnBack={handleOnBack}
        jobName={jobName}
        {...props}
      />
      <MainLayout.Container direction="column" bgColor={palette.Grey100}>
        <Table
          search=""
          sort=""
          handleOnSelect={handleOnSelect}
          disallowSetup={disallowSetup}
          isStopAndStartRun={isStopAndStartRun}
          newWorkOrderId={newWorkOrderId}
          showAllTypes={showAllTypes}
        />
      </MainLayout.Container>
    </MainLayout.Column>
  );
};

SelectActivityType.propTypes = {
  jobName: PropTypes.string.isRequired,
  handleOnBack: PropTypes.func,
  handleOnSelect: PropTypes.func.isRequired,
  handleOnClose: PropTypes.func.isRequired,
  job: PropTypes.object,
  newWorkOrderId: PropTypes.string,
  isStopAndStartRun: PropTypes.bool,
  workOrderOp: PropTypes.object,
};

export { SelectActivityType };
